import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Radio,
  Space,
  Switch,
  message,
} from 'antd';

const NO_MATCH_OPTIONS = [
  {
    value: 'best_effort',
    label: 'Upload anyway without a provider',
    description: 'The document still files into the Results tab; staff can set the ordering provider in EMA afterward.',
  },
  {
    value: 'hold_for_review',
    label: 'Hold for manual review',
    description: 'The upload fails in Medsender so someone can assign the ordering provider before it files.',
  },
];

const settingEnabled = (value) => value === true || value === 'true';

const EmaResultsProviderSettings = ({
  settings, providerCount, updateSettings, requestedEnabled,
}) => {
  const [enabled, setEnabled] = useState(false);
  const [onNoMatch, setOnNoMatch] = useState('best_effort');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const nextSettings = settings || {};

    // requestedEnabled overrides the saved state in both directions (the
    // table switch's requested value); null means open without an override.
    setEnabled(typeof requestedEnabled === 'boolean' ? requestedEnabled : settingEnabled(nextSettings.enabled));
    setOnNoMatch(nextSettings.on_no_match || 'best_effort');
  }, [settings, requestedEnabled]);

  const handleSave = async () => {
    setSaving(true);

    try {
      await updateSettings({
        enabled,
        on_no_match: onNoMatch,
      });
      message.success('Settings saved successfully');
    } catch (error) {
      const errorMsg = error?.response?.data?.error || 'Failed to save settings';
      message.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        message="When a document files into one of your Results categories, Medsender matches the AI-extracted ordering provider against your synced EMA provider list and assigns it automatically."
        style={{ marginBottom: 16 }}
      />

      {providerCount === 0 && (
        <Alert
          type="warning"
          showIcon
          message="No active providers have been synced from your EMA yet. Provider matching is skipped until your provider list is available. Check the Manage Providers tab."
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <Space>
          <span>Automatically assign ordering provider:</span>
          <Switch checked={enabled} onChange={setEnabled} />
        </Space>
      </div>

      {enabled && (
        <div>
          <div style={{ marginBottom: 8 }}>
            If we can&apos;t confidently match the provider:
          </div>
          <Radio.Group
            value={onNoMatch}
            onChange={(event) => setOnNoMatch(event.target.value)}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {NO_MATCH_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value} style={{ lineHeight: 1.5 }}>
                <div>
                  <strong>{option.label}</strong>
                </div>
                <div style={{ color: '#666' }}>
                  {option.description}
                </div>
              </Radio>
            ))}
          </Radio.Group>
        </div>
      )}

      {providerCount > 0 && (
        <div style={{ color: '#666', marginTop: 16 }}>
          {`Matching against ${providerCount} active provider${providerCount === 1 ? '' : 's'} synced from your EMA. Manage them in the Manage Providers tab.`}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <Button type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};

EmaResultsProviderSettings.defaultProps = {
  settings: {},
  providerCount: null,
  requestedEnabled: null,
};

EmaResultsProviderSettings.propTypes = {
  settings: PropTypes.object,
  providerCount: PropTypes.number,
  updateSettings: PropTypes.func.isRequired,
  requestedEnabled: PropTypes.bool,
};

export default EmaResultsProviderSettings;
